import { effect, inject, provideAppInitializer, untracked } from '@angular/core';
import { Settings } from 'luxon';
import { DateTimeService } from './datetime.service';

/**
 * Points Luxon's global default zone at the account's timezone, so a bare
 * `DateTime.fromISO(x)` / `DateTime.now()` anywhere in the app reads in the
 * account's wall clock instead of the browser machine's.
 *
 * Reactive on purpose: `ownerSettings` lands asynchronously (the owner-settings
 * resolver, on the first navigation) and can change afterwards — a one-shot
 * read at bootstrap would freeze the zone at the machine-zone fallback
 * `getUserZone()` returns while nothing is loaded.
 *
 * ## What this changes
 *
 * Only call sites that do NOT name a zone. Everything that already passes
 * `{ zone: 'utc' }` — the whole `toUtcObject` / `toLocalObject` / date-input
 * pipeline — is untouched. Bare calls previously resolved in the machine's
 * zone, which is invisible while the machine sits in the same zone as the
 * account, and wrong by whole hours when it does not. That is the case this
 * fixes, and equally the case where it moves rendered values.
 */
export function initializeLuxonZone(): void {
  const dateTimeService = inject(DateTimeService);

  effect(() => {
    const timezone = dateTimeService.getUserZone();
    untracked(() => {
      Settings.defaultZone = timezone;
    });
  });
}

/**
 * Provider for Luxon's default-zone binding, shared by every app shell.
 */
export const LUXON_ZONE_INITIALIZER = provideAppInitializer(initializeLuxonZone);
